/**
 * useAutoSave — debounced persistence helper.
 *
 * Usage:
 *   const { status, lastSavedAt, pendingSave, error, flush } = useAutoSave(
 *     value,                       // current value (any serializable shape)
 *     async (v) => api.put(...),   // save fn
 *     { delay: 1500, enabled: true }
 *   );
 *
 * Behavior:
 *  - Skips the initial mount (won't save the value that just loaded).
 *  - Coalesces rapid changes within `delay` ms into a single save.
 *  - If a save is in-flight when a new change arrives, queues the next save
 *    after the current one completes.
 *  - status: "idle" → "saving" → "saved" → "idle" (after 4s).
 *  - On error → status="error" until next successful save.
 */
import { useEffect, useRef, useState, useCallback } from "react";

export function useAutoSave(value, saveFn, { delay = 1500, enabled = true } = {}) {
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const initRef = useRef(true);
  const savingRef = useRef(false);
  const queuedRef = useRef(null);          // latest value waiting after current save
  const lastSentRef = useRef(undefined);   // last value successfully POSTed
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;

  const performSave = useCallback(async (v) => {
    savingRef.current = true;
    setStatus("saving");
    setError(null);
    try {
      await saveFnRef.current(v);
      lastSentRef.current = v;
      setLastSavedAt(new Date());
      setStatus("saved");
      // After a moment, fade back to idle so the chip doesn't shout forever.
      setTimeout(() => {
        setStatus((s) => (s === "saved" ? "idle" : s));
      }, 4000);
    } catch (e) {
      setError(e);
      setStatus("error");
    } finally {
      savingRef.current = false;
      // If another change arrived while we were saving, run it now.
      if (queuedRef.current !== null) {
        const next = queuedRef.current;
        queuedRef.current = null;
        performSave(next);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    // Skip the first render (initial value just loaded — nothing to save).
    if (initRef.current) {
      initRef.current = false;
      lastSentRef.current = value;
      return;
    }
    // No-op if the value is structurally identical to the last successful save.
    try {
      if (JSON.stringify(value) === JSON.stringify(lastSentRef.current)) return;
    } catch {
      /* unserializable — fall through to save */
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (savingRef.current) {
        queuedRef.current = value;
      } else {
        performSave(value);
      }
    }, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delay, enabled, performSave]);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (savingRef.current) return;
    if (lastSentRef.current === value) return;
    await performSave(value);
  }, [value, performSave]);

  // Warn the user if they try to navigate away with a pending save.
  useEffect(() => {
    function onBeforeUnload(e) {
      if (status === "saving" || timerRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [status]);

  return { status, lastSavedAt, error, flush, pendingSave: !!timerRef.current };
}
